import * as React from 'react';
import { createContext, useContext, useState, useEffect, ReactNode, useCallback, useRef } from 'react';
import { Observable } from 'rxjs';
import { IExtensionGroup, IGroupComponent, ISegmentRule } from '../../models/extension-group.models';
import { ExtensionsGroupType, ICheckoutExtensions } from '@pdqdev/pdq-shared/dist/interfaces/ICheckoutExtensions';
import { RuleEngineState } from '@app/react-common/components/rule-engine-v2/models/RuleEngineState';
import {
  mapRuleEngineApiDtoToRuleEngineState,
  mapRuleEngineStateToRuleEngineApiDto,
} from '@app/react-common/components/rule-engine-v2/utils/ruleEngineMapper';
import { RuleEngineApiDto } from '@app/pages/checkout-app/checkout-layout/main/components/ab-testing/react/models/AbTestRulesApiDto';
import { useAppContext } from '@app/react-common/AppContext';
import { useExtensionsApi } from '@app/react-common/api/useExtensionsApi';

export interface ISegmentRuleActions {
  updateSegmentRuleState: (groupId: number, segmentId: number, ruleEngineState: RuleEngineState) => void;
  saveSegmentRuleToApi: (groupId: number, segmentId: number) => Promise<void>;
  checkIfSegmentRuleHasBeenEdited: (groupId: number, segmentId: number) => boolean;
  cancelSegmentRuleEdits: (groupId: number, segmentId: number) => void;
  expandSegmentRule: (groupId: number, segmentId: number) => void;
  renameSegmentRule: (groupId: number, segmentId: number, newName: string) => Promise<void>;
  removeSegmentRule: (groupId: number, segmentId: number) => Promise<void>;
  createSegmentRule: (groupId: number, data: { name: string; rules?: any }) => void;
}

export interface IExtensionActions {
  addExtensionToGroup: (groupId: number, extensionId: number, enabled: boolean) => Promise<void>;
  removeExtensionFromGroup: (groupId: number, componentId: number) => Promise<void>;
  updateComponentPriorities: (groupId: number, components: { id: number; priority: number }[]) => Promise<void>;
  toggleExtensionGroupComponent: (groupId: number, componentId: number, enabled: boolean) => Promise<void>;
}

export interface IGroupActions {
  createGroup: (name: string, description: string, groupType: ExtensionsGroupType) => Promise<void>;
  renameGroup: (groupId: number, name: string, description: string) => Promise<void>;
  deleteGroup: (groupId: number) => Promise<void>;
  toggleGroupEnabled: (groupId: number, enabled: boolean) => Promise<void>;
}

interface ExtensionGroupsContextValue {
  extensionGroups: IExtensionGroup[];
  selectedGroup: IExtensionGroup | null;
  isLoading: boolean;
  segmentRuleActions: ISegmentRuleActions;
  availableExtensions: ICheckoutExtensions[];
  extensionActions: IExtensionActions;
  groupActions: IGroupActions;
  setSelectedGroup: (group: IExtensionGroup | null) => void;
}

interface ExtensionGroupsProviderProps {
  children: ReactNode;
  extensionGroups$: Observable<IExtensionGroup[]>;
  extensions$: Observable<ICheckoutExtensions[]>;
  initialGroupId?: number | null;
}

const ExtensionGroupsContext = createContext<ExtensionGroupsContextValue | undefined>(undefined);

export const ExtensionGroupsProvider: React.FC<ExtensionGroupsProviderProps> = ({
  children,
  extensionGroups$,
  extensions$,
  initialGroupId,
}) => {
  const [extensionGroups, setExtensionGroups] = useState<IExtensionGroup[]>([]);
  const [selectedGroup, setSelectedGroup] = useState<IExtensionGroup | null>(null);
  const [availableExtensions, setAvailableExtensions] = useState<ICheckoutExtensions[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { showToast, showFullPageLoader, angularRedirect } = useAppContext();
  const extensions$Ref = useRef(extensions$);
  extensions$Ref.current = extensions$;
  // Subscribe to extensions$
  useEffect(() => {
    if (!extensions$) return;

    const subscription = extensions$.subscribe((extensions) => {
      setAvailableExtensions(extensions || []);
    });

    return () => subscription.unsubscribe();
  }, [extensions$]);

  const failureHandler = useCallback(async () => {
    showToast({ message: 'An error has occurred. Please contact support if the problem persists.', mode: 'danger' });
    showFullPageLoader(false);
  }, [showToast, showFullPageLoader]);

  const extensionsApi = useExtensionsApi(failureHandler);
  const handleSetSelectedGroup = useCallback((group: IExtensionGroup | null) => {
    setSelectedGroup(group);
    if (group) {
      angularRedirect(`/convert/extension-groups/${group.id}`);
    }
  }, []);
  const mapSegmentRules = (groups: IExtensionGroup[]): IExtensionGroup[] => {
    if (!groups) return [];

    return groups.map((group) => ({
      ...group,
      segmentRules: (group.segmentRules || []).map((segment, segmentIdx) => ({
        ...segment,
        expanded: false,
        internalId: segment.internalId ?? segmentIdx,
        rerender: segment.rerender ? !segment.rerender : false,
        rules: segment?.rules
          ? mapRuleEngineApiDtoToRuleEngineState(segment.rules as unknown as RuleEngineApiDto)
          : { ruleGroups: [], matchAllRuleGroups: true },
        rulesBeforeEdit: segment?.rules
          ? mapRuleEngineApiDtoToRuleEngineState(segment.rules as unknown as RuleEngineApiDto)
          : { ruleGroups: [], matchAllRuleGroups: true },
      })),
    }));
  };

  useEffect(() => {
    if (!extensionGroups$) {
      setIsLoading(false);
      return;
    }

    const subscription = extensionGroups$.subscribe((groups) => {
      const mappedExtensionGroups = mapSegmentRules(groups);
      setExtensionGroups(mappedExtensionGroups);
      setIsLoading(false);

      if (!selectedGroup && mappedExtensionGroups.length > 0) {
        let groupToSelect: IExtensionGroup | null = null;

        // If initialGroupId is provided, try to select that group
        if (initialGroupId) {
          const targetGroup = mappedExtensionGroups.find((g) => g.id === initialGroupId);
          if (targetGroup) {
            groupToSelect = targetGroup;
          }
        }
        if (!groupToSelect) {
          groupToSelect = mappedExtensionGroups[0];
        }
        // Otherwise select the first group
        setSelectedGroup(groupToSelect);
        angularRedirect(`/convert/extension-groups/${groupToSelect.id}`);
      } else if (selectedGroup) {
        const updatedSelectedGroup = mappedExtensionGroups.find((g) => g.id === selectedGroup.id);
        if (updatedSelectedGroup) {
          setSelectedGroup(updatedSelectedGroup);
        }
      }
    });

    return () => subscription.unsubscribe();
  }, [extensionGroups$, initialGroupId]);

  const segmentRuleActions: ISegmentRuleActions = {
    updateSegmentRuleState: (groupId: number, segmentId: number, ruleEngineState: RuleEngineState) => {
      setExtensionGroups((groups) => {
        return groups.map((group) => {
          if (group.id === groupId) {
            const updatedGroup = {
              ...group,
              segmentRules: group.segmentRules?.map((segment) => {
                if (segment.id === segmentId || segment.internalId === segmentId) {
                  return {
                    ...segment,
                    rules: ruleEngineState,
                  };
                }
                return segment;
              }),
            };
            if (selectedGroup?.id === groupId) {
              setSelectedGroup(updatedGroup);
            }
            return updatedGroup;
          }
          return group;
        });
      });
    },

    saveSegmentRuleToApi: async (groupId: number, segmentId: number) => {
      const group = extensionGroups.find((g) => g.id === groupId);
      const segment = group?.segmentRules?.find((s) => s.id === segmentId || s.internalId === segmentId);

      if (!segment) return;

      showFullPageLoader(true);

      try {
        const basePayload = {
          name: segment.name,
          matchAllRules: segment.matchAllRules,
          rules: mapRuleEngineStateToRuleEngineApiDto(segment.rules),
        };

        const response = segment.id
          ? await extensionsApi.put(`/admin/v1/extension-groups/${groupId}/segment-rules/${segment.id}`, {
              ...basePayload,
              id: segment.id,
              extensionsGroupId: groupId,
            })
          : await extensionsApi.post(`/admin/v1/extension-groups/${groupId}/segment-rules`, basePayload);

        const savedSegmentId = segment.id || response.data.id;

        setExtensionGroups((groups) => {
          return groups.map((g) => {
            if (g.id === groupId) {
              const updatedGroup = {
                ...g,
                segmentRules: g.segmentRules?.map((s) => {
                  if (s.id === segmentId || s.internalId === segmentId) {
                    return {
                      ...s,
                      id: savedSegmentId,
                      rulesBeforeEdit: s.rules,
                      expanded: false,
                      rerender: !s.rerender,
                    };
                  }
                  return s;
                }),
              };
              if (selectedGroup?.id === groupId) {
                setSelectedGroup(updatedGroup);
              }
              return updatedGroup;
            }
            return g;
          });
        });

        showToast({ message: 'Changes saved.', mode: 'success' });
      } catch (error) {
        console.error('Error saving segment rule:', error);
      } finally {
        showFullPageLoader(false);
      }
    },

    checkIfSegmentRuleHasBeenEdited: (groupId: number, segmentId: number): boolean => {
      const group = extensionGroups.find((g) => g.id === groupId);
      const segment = group?.segmentRules?.find((s) => s.id === segmentId || s.internalId === segmentId);

      if (!segment) return false;

      return JSON.stringify(segment.rules) !== JSON.stringify(segment.rulesBeforeEdit);
    },

    cancelSegmentRuleEdits: (groupId: number, segmentId: number) => {
      setExtensionGroups((groups) => {
        return groups.map((g) => {
          if (g.id === groupId) {
            const updatedGroup = {
              ...g,
              segmentRules: g.segmentRules?.map((s) => {
                if (s.id === segmentId || s.internalId === segmentId) {
                  return {
                    ...s,
                    rules: { ...s.rulesBeforeEdit },
                    rerender: !s.rerender,
                  };
                }
                return s;
              }),
            };
            if (selectedGroup?.id === groupId) {
              setSelectedGroup(updatedGroup);
            }
            return updatedGroup;
          }
          return g;
        });
      });
    },

    expandSegmentRule: (groupId: number, segmentId: number) => {
      setExtensionGroups((groups) => {
        return groups.map((g) => {
          if (g.id === groupId) {
            const updatedGroup = {
              ...g,
              segmentRules: g.segmentRules?.map((s) => {
                if (s.id === segmentId || s.internalId === segmentId) {
                  return {
                    ...s,
                    expanded: !s.expanded,
                  };
                }
                return s;
              }),
            };
            if (selectedGroup?.id === groupId) {
              setSelectedGroup(updatedGroup);
            }
            return updatedGroup;
          }
          return g;
        });
      });
    },

    renameSegmentRule: async (groupId: number, segmentId: number, newName: string) => {
      const group = extensionGroups.find((g) => g.id === groupId);
      const segment = group?.segmentRules?.find((s) => s.id === segmentId || s.internalId === segmentId);

      if (!segment) return;

      try {
        await extensionsApi.patch(`/admin/v1/extension-groups/${groupId}/segment-rules/${segment.id}`, {
          name: newName,
        });

        setExtensionGroups((groups) => {
          return groups.map((g) => {
            if (g.id === groupId) {
              const updatedGroup = {
                ...g,
                segmentRules: g.segmentRules?.map((s) => {
                  if (s.id === segmentId || s.internalId === segmentId) {
                    return {
                      ...s,
                      name: newName,
                    };
                  }
                  return s;
                }),
              };
              if (selectedGroup?.id === groupId) {
                setSelectedGroup(updatedGroup);
              }
              return updatedGroup;
            }
            return g;
          });
        });

        showToast({ message: 'Segment renamed.', mode: 'success' });
      } catch (error) {
        console.error('Error renaming segment:', error);
      }
    },

    removeSegmentRule: async (groupId: number, segmentId: number) => {
      const group = extensionGroups.find((g) => g.id === groupId);
      const segment = group?.segmentRules?.find((s) => s.id === segmentId || s.internalId === segmentId);

      if (!segment) return;

      showFullPageLoader(true);

      try {
        if (segment.id) {
          await extensionsApi.delete(`/admin/v1/extension-groups/${groupId}/segment-rules/${segment.id}`);
        }

        setExtensionGroups((groups) => {
          return groups.map((g) => {
            if (g.id === groupId) {
              const updatedGroup = {
                ...g,
                segmentRules: g.segmentRules?.filter((s) => s.id !== segmentId && s.internalId !== segmentId),
              };
              if (selectedGroup?.id === groupId) {
                setSelectedGroup(updatedGroup);
              }
              return updatedGroup;
            }
            return g;
          });
        });

        showToast({ message: 'Segment deleted.', mode: 'success' });
      } catch (error) {
        console.error('Error removing segment:', error);
      } finally {
        showFullPageLoader(false);
      }
    },

    createSegmentRule: (groupId: number, data: { name: string; rules?: any }) => {
      setExtensionGroups((groups) => {
        return groups.map((g) => {
          if (g.id === groupId) {
            const maxInternalId =
              g.segmentRules && g.segmentRules.length > 0
                ? Math.max(...g.segmentRules.map((s) => s.internalId ?? 0)) + 1
                : 0;

            const newSegment: ISegmentRule = {
              internalId: maxInternalId,
              name: data.name,
              matchAllRules: true,
              extensionsGroupId: groupId,
              expanded: true,
              rerender: false,
              rules: data.rules || { matchAllRuleGroups: true, ruleGroups: [] },
              rulesBeforeEdit: data.rules || { matchAllRuleGroups: true, ruleGroups: [] },
            };

            const updatedGroup = {
              ...g,
              segmentRules: [newSegment, ...(g.segmentRules || [])],
            };
            if (selectedGroup?.id === groupId) {
              setSelectedGroup(updatedGroup);
            }
            return updatedGroup;
          }
          return g;
        });
      });
    },
  };

  const extensionActions: IExtensionActions = {
    addExtensionToGroup: async (groupId: number, extensionId: number, enabled: boolean) => {
      const group = extensionGroups.find((g) => g.id === groupId);
      if (!group) return;

      showFullPageLoader(true);

      try {
        const response = await extensionsApi.post(`/admin/v1/extension-groups/${groupId}/components`, {
          extensionComponentId: extensionId,
          priority: (group.groupComponents?.length || 0) + 1,
          enabled,
        });

        const newComponent = response.data as IGroupComponent;

        setExtensionGroups((groups) => {
          return groups.map((g) => {
            if (g.id === groupId) {
              const updatedGroup = {
                ...g,
                groupComponents: [...(g.groupComponents || []), newComponent],
              };
              if (selectedGroup?.id === groupId) {
                setSelectedGroup(updatedGroup);
              }
              return updatedGroup;
            }
            return g;
          });
        });

        showToast({ message: 'Extension added to group.', mode: 'success' });
      } catch (error) {
        console.error('Error adding extension to group:', error);
      } finally {
        showFullPageLoader(false);
      }
    },

    removeExtensionFromGroup: async (groupId: number, componentId: number) => {
      const group = extensionGroups.find((g) => g.id === groupId);
      if (!group) return;

      showFullPageLoader(true);

      try {
        await extensionsApi.delete(`/admin/v1/extension-groups/${groupId}/components/${componentId}`);

        setExtensionGroups((groups) => {
          return groups.map((g) => {
            if (g.id === groupId) {
              const updatedGroup = {
                ...g,
                groupComponents: g.groupComponents?.filter((c) => c.id !== componentId),
              };
              if (selectedGroup?.id === groupId) {
                setSelectedGroup(updatedGroup);
              }
              return updatedGroup;
            }
            return g;
          });
        });

        showToast({ message: 'Extension removed from group.', mode: 'success' });
      } catch (error) {
        console.error('Error removing extension from group:', error);
      } finally {
        showFullPageLoader(false);
      }
    },

    updateComponentPriorities: async (groupId: number, components: { id: number; priority: number }[]) => {
      const group = extensionGroups.find((g) => g.id === groupId);
      if (!group) return;

      showFullPageLoader(true);

      try {
        const response = await extensionsApi.patch(`/admin/v1/extension-groups/${groupId}/components/priorities`, {
          components,
        });

        const updatedGroup = response.data as IExtensionGroup;

        setExtensionGroups((groups) => {
          return groups.map((g) => {
            if (g.id === groupId) {
              const newGroup = {
                ...g,
                groupComponents: updatedGroup.groupComponents,
              };
              if (selectedGroup?.id === groupId) {
                setSelectedGroup(newGroup);
              }
              return newGroup;
            }
            return g;
          });
        });
      } catch (error) {
        console.error('Error updating component priorities:', error);
        throw error;
      } finally {
        showFullPageLoader(false);
      }
    },

    toggleExtensionGroupComponent: async (groupId: number, componentId: number, enabled: boolean) => {
      const group = extensionGroups.find((g) => g.id === groupId);
      if (!group) return;

      try {
        await extensionsApi.patch(`/admin/v1/extension-groups/${groupId}/components/update`, {
          components: [{ id: componentId, enabled }],
        });

        setExtensionGroups((groups) => {
          return groups.map((g) => {
            if (g.id === groupId) {
              const updatedGroup = {
                ...g,
                groupComponents: g.groupComponents?.map((c) => (c.id === componentId ? { ...c, enabled } : c)),
              };
              if (selectedGroup?.id === groupId) {
                setSelectedGroup(updatedGroup);
              }
              return updatedGroup;
            }
            return g;
          });
        });
      } catch (error) {
        console.error('Error toggling component enabled:', error);
      }
    },
  };

  const groupActions: IGroupActions = {
    createGroup: async (name: string, description: string, groupType: ExtensionsGroupType) => {
      showFullPageLoader(true);

      try {
        const response = await extensionsApi.post('/admin/v1/extension-groups', {
          name,
          description,
          type: groupType,
          enabled: true,
        });

        const newGroup = response.data as IExtensionGroup;

        setExtensionGroups((groups) => [...groups, newGroup]);
        setSelectedGroup(newGroup);

        showToast({ message: 'Extension group created.', mode: 'success' });
      } catch (error) {
        console.error('Error creating extension group:', error);
      } finally {
        showFullPageLoader(false);
      }
    },

    renameGroup: async (groupId: number, name: string, description: string) => {
      showFullPageLoader(true);

      try {
        await extensionsApi.patch(`/admin/v1/extension-groups/${groupId}`, {
          name,
          description,
        });

        setExtensionGroups((groups) => {
          return groups.map((g) => {
            if (g.id === groupId) {
              const updatedGroup = { ...g, name, description };
              if (selectedGroup?.id === groupId) {
                setSelectedGroup(updatedGroup);
              }
              return updatedGroup;
            }
            return g;
          });
        });

        showToast({ message: 'Extension group updated.', mode: 'success' });
      } catch (error) {
        console.error('Error renaming extension group:', error);
      } finally {
        showFullPageLoader(false);
      }
    },

    deleteGroup: async (groupId: number) => {
      showFullPageLoader(true);

      try {
        await extensionsApi.delete(`/admin/v1/extension-groups/${groupId}`);

        setExtensionGroups((groups) => {
          const updatedGroups = groups.filter((g) => g.id !== groupId);
          if (selectedGroup?.id === groupId) {
            setSelectedGroup(updatedGroups.length > 0 ? updatedGroups[0] : null);
          }
          return updatedGroups;
        });

        showToast({ message: 'Extension group deleted.', mode: 'success' });
      } catch (error) {
        console.error('Error deleting extension group:', error);
      } finally {
        showFullPageLoader(false);
      }
    },

    toggleGroupEnabled: async (groupId: number, enabled: boolean) => {
      showFullPageLoader(true);

      try {
        await extensionsApi.patch(`/admin/v1/extension-groups/${groupId}`, {
          enabled,
        });

        setExtensionGroups((groups) => {
          return groups.map((g) => {
            if (g.id === groupId) {
              const updatedGroup = { ...g, enabled };
              if (selectedGroup?.id === groupId) {
                setSelectedGroup(updatedGroup);
              }
              return updatedGroup;
            }
            return g;
          });
        });

        showToast({ message: enabled ? 'Extension group enabled.' : 'Extension group disabled.', mode: 'success' });
      } catch (error) {
        console.error('Error toggling extension group:', error);
      } finally {
        showFullPageLoader(false);
      }
    },
  };

  const contextValue: ExtensionGroupsContextValue = {
    extensionGroups,
    selectedGroup,
    isLoading,
    setSelectedGroup: handleSetSelectedGroup,
    segmentRuleActions,
    availableExtensions,
    extensionActions,
    groupActions,
  };

  return <ExtensionGroupsContext.Provider value={contextValue}>{children}</ExtensionGroupsContext.Provider>;
};

export const useExtensionGroups = (): ExtensionGroupsContextValue => {
  const context = useContext(ExtensionGroupsContext);
  if (!context) {
    throw new Error('useExtensionGroups must be used within an ExtensionGroupsProvider');
  }
  return context;
};
